import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from '../../api/apiClient';

import {
	CreateTableNew as CreateTable,
	SearchBar,
	Button,
	GeneralMessage,
	useGeneralMessage,
	LocationIdSelectWithAll,
	TimeKeeperSelectWithAll,
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { Select } from "../../common/Select";
import { EmployeePickerField } from "../../common/selectComponents/EmployeePicker";

function TimeKeeper({ user, isHideSideMenu }) {
	// Use the hook for message management
	const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

	var headers = [
		"ID",
		"Location",
		"Time Keeper",
		"Shift Schedule",
		"Actions"
	]

	var accessors = [
		"id",
		"locationDescription",
		"employeePersonalInformationFullName",
		"shiftScheduleShiftName",
		"actions"
	]

	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [loadingRequest, setLoadingRequest] = useState(false);
	const [data, setData] = useState([]);
	const [selectedData, setSelectedData] = useState(null);

	// Form state
	const [form, setForm] = useState({
		locationId: "",
		locationDescription: "",
		employeePersonalInformationId: "",
		employeeData: null,
		shiftScheduleId: "",
		shiftScheduleName: ""
	});

	// Filters state
	const [locationFilter, setLocationFilter] = useState("0");
	const [timeKeeperFilter, setTimeKeeperFilter] = useState("0");

	// Filter loading state
	const [filterLoading, setFilterLoading] = useState(false);
	const filterLoadingTimerRef = useRef(null);

	// Function to handle filter changes with loading spinner
	const handleLocationFilterChange = (value) => {
		setLocationFilter(value);
		triggerFilterLoading();
	};

	const handleTimeKeeperFilterChange = (value) => {
		setTimeKeeperFilter(value);
		triggerFilterLoading();
	};

	// Function to trigger filter loading spinner for at least 600ms
	const triggerFilterLoading = () => {
		// Clear any existing timer
		if (filterLoadingTimerRef.current) {
			clearTimeout(filterLoadingTimerRef.current);
		}

		// Show loading spinner
		setFilterLoading(true);

		// Set minimum loading time of 600ms
		filterLoadingTimerRef.current = setTimeout(() => {
			setFilterLoading(false);
			filterLoadingTimerRef.current = null;
		}, 600);
	};

	const resetLocalVariables = () => {
		setForm({
			locationId: "",
			locationDescription: "",
			employeePersonalInformationId: "",
			employeeData: null,
			shiftScheduleId: "",
			shiftScheduleName: ""
		});
		setSelectedData(null);
	}

	// Retrieve
	const [filterData, setFilterData] = useState([]);
	// Add
	const [showAddForm, setShowAddForm] = useState(false);
	// Update
	const [showEditForm, setShowEditForm] = useState(false);
	// Delete
	const [showDeleteForm, setShowDeleteForm] = useState(false);
	// Employee Select
	const [showEmployeeSelectForm, setShowEmployeeSelectForm] = useState(false);
	const [selectionMode, setSelectionMode] = useState("timekeeper"); // "timekeeper"

	const apiUrl = domain + "/api/TimeKeeper";

	const fetchData = useCallback(async () => {
		try {
			// Don't set main loading state here since we have filterLoading
			const response = await axios.get(apiUrl, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Apply filters if not "0" (All)
			let filteredData = response.data;
			if (locationFilter.toString() !== "0") {
				filteredData = filteredData.filter(data =>
					data.locationId.toString() === locationFilter.toString()
				);
			}

			if (timeKeeperFilter.toString() !== "0") {
				filteredData = filteredData.filter(data =>
					data.employeePersonalInformationId.toString() === timeKeeperFilter.toString()
				);
			}

			setData(filteredData);
			setFilterData(filteredData);
		} catch (error) {
			console.error('Error fetching data:', error);
			showGeneralMessage("Failed to fetch timekeeper data", false);
		}
	}, [apiUrl, locationFilter, timeKeeperFilter]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (filterLoadingTimerRef.current) {
				clearTimeout(filterLoadingTimerRef.current);
			}
		};
	}, []);

	const handleFilter = (value) => {
		setCurrentPage(1);
		const lowercaseValue = value.toLowerCase();

		if (!value) {
			// If input is empty, reset to filtered list
			setData(filterData);
			return;
		}

		const res = filterData.filter((f) => {
			const match1 = f.employeePersonalInformationFullName?.toString().toLowerCase().includes(lowercaseValue);
			const match2 = f.locationDescription?.toString().toLowerCase().includes(lowercaseValue);
			const match3 = f.shiftScheduleShiftName?.toString().toLowerCase().includes(lowercaseValue);
			const match4 = f.id?.toString().startsWith(lowercaseValue);
			return match1 || match2 || match3 || match4;
		});

		setData(res);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		// Validation
		if (!form.locationId || !form.employeePersonalInformationId || !form.shiftScheduleId) {
			showGeneralMessage("Please fill all required fields", false);
			return;
		}

		// Prepare data for API
		const apiData = {
			employeePersonalInformationId: form.employeePersonalInformationId,
			shiftScheduleId: form.shiftScheduleId,
			locationId: form.locationId
		};

		try {
			setLoadingRequest(true);
			const response = await axios.post(apiUrl, apiData, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Audit trail logic
			await SetAuditTrail({
				setupArea: "Time Keeper",
				actionTaken: "insert",
				field: "Add",
				newValue: `${response.data.locationDescription}`,
				oldValue: "-",
				dateSaved: new Date(),
				savedById: user.id
			});

			await fetchData();
			showGeneralMessage("Time Keeper created successfully!", true);
			closeAddHandleClick();
		} catch (error) {
			console.error('Error creating data:', error.response || error);

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.message ||
				"Failed to create time keeper";
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleUpdate = async (event) => {
		event.preventDefault();

		if (!selectedData?.id) {
			showGeneralMessage("No record selected for update", false);
			return;
		}

		// Validation
		if (!form.locationId || !form.employeePersonalInformationId || !form.shiftScheduleId) {
			showGeneralMessage("Please fill all required fields", false);
			return;
		}

		const apiData = {
			employeePersonalInformationId: form.employeePersonalInformationId,
			shiftScheduleId: form.shiftScheduleId,
			locationId: form.locationId
		};

		try {
			setLoadingRequest(true);
			await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Audit trail logic
			let auditInfo = [];

			if (apiData.employeePersonalInformationId.toString() !== selectedData.employeePersonalInformationId.toString()) {
				auditInfo.push({
					setupArea: "Time Keeper",
					actionTaken: "update",
					field: "Time Keeper",
					newValue: `${form.employeeData?.fullName || apiData.employeePersonalInformationId}`,
					oldValue: `${selectedData.employeePersonalInformationFullName || selectedData.employeePersonalInformationId}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.shiftScheduleId.toString() !== selectedData.shiftScheduleId.toString()) {
				auditInfo.push({
					setupArea: "Time Keeper",
					actionTaken: "update",
					field: "Shift Schedule",
					newValue: `${apiData.shiftScheduleId}`,
					oldValue: `${selectedData.shiftScheduleId}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (apiData.locationId.toString() !== selectedData.locationId.toString()) {
				auditInfo.push({
					setupArea: "Time Keeper",
					actionTaken: "update",
					field: "Location",
					newValue: `${apiData.locationId}`,
					oldValue: `${selectedData.locationId}`,
					dateSaved: new Date(),
					savedById: user.id,
				});
			}

			if (auditInfo.length > 0) {
				await SetAuditTrailBulk({ data: auditInfo });
			}

			setShowEditForm(false);
			await fetchData();
			showGeneralMessage("Time Keeper updated successfully!", true);
			closeEditHandleClick();
		} catch (error) {
			console.error('Error updating data:', error.response || error);

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.message ||
				"Failed to update time keeper";
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	const handleDelete = async () => {
		if (!selectedData?.id) {
			showGeneralMessage("No record selected for deletion", false);
			return;
		}

		try {
			setLoadingRequest(true);
			await axios.delete(`${apiUrl}/${selectedData.id}`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			await fetchData();
			showGeneralMessage("Time Keeper deleted successfully!", true);
			closeDeleteHandleClick();
		} catch (error) {
			console.error('Error deleting data:', error.response || error);

			const errorMessage = error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.message ||
				"Failed to delete time keeper";
			showGeneralMessage(errorMessage, false);
		} finally {
			setLoadingRequest(false);
		}
	};

	// Employee selection functions
	const openEmployeeSelect = () => {
		setSelectionMode("timekeeper");
		setShowEmployeeSelectForm(true);
	};

	const handleSelectEmployee = (employee) => {
		setForm(prev => ({
			...prev,
			employeePersonalInformationId: employee.id,
			employeeData: {
				id: employee.id,
				fullName: employee.fullName,
				employeeId: employee.employeeId,
				dateHired: employee.employeeInformationDateEmployed,
				department: employee.employeeInformationDepartmentDescription,
				position: employee.employeeInformationPositionDescription
			}
		}));
		setShowEmployeeSelectForm(false);
	};

	const clearEmployee = () => {
		setForm(prev => ({
			...prev,
			employeePersonalInformationId: "",
			employeeData: null
		}));
	};

	const editHandleClick = (data) => {
		setSelectedData(data);

		setForm({
			locationId: data.locationId || "",
			locationDescription: data.locationDescription || "",
			employeePersonalInformationId: data.employeePersonalInformationId || "",
			employeeData: {
				id: data.employeePersonalInformationId,
				fullName: data.employeePersonalInformationFullName,
				employeeId: data.employeePersonalInformationEmployeeId// Add this line
			},
			shiftScheduleId: data.shiftScheduleId || "",
			shiftScheduleName: data.shiftScheduleShiftName || ""
		});
		openEditHandleClick();
	};

	const deleteHandleClick = (data) => {
		setSelectedData(data);
		openDeleteHandleClick();
	};

	const openAddHandleClick = () => {
		setShowAddForm(true);
	};

	const closeAddHandleClick = () => {
		setShowAddForm(false);
		resetLocalVariables();
	};

	const openEditHandleClick = () => {
		setShowEditForm(true);
	};

	const closeEditHandleClick = () => {
		setShowEditForm(false);
		resetLocalVariables();
	};

	const openDeleteHandleClick = () => {
		setShowDeleteForm(true);
	};

	const closeDeleteHandleClick = () => {
		setShowDeleteForm(false);
		resetLocalVariables();
	};

	const actions = [
		{
			name: "Edit",
			tooltip: "edit",
			icon: <EditIcon fontSize="small" />,
			className: "bg-green-500 hover:bg-green-600 flex items-center",
			function: editHandleClick,
			visible: true,
		},
		{
			name: "Delete",
			tooltip: "delete",
			icon: <DeleteIcon fontSize="small" />,
			className: "bg-red-500 hover:bg-red-600 flex items-center",
			function: deleteHandleClick,
			visible: true,
		},
	];

	return (
		<div>
			<div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
				<div className="LeftSpace"></div>
				<div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
					<div className="MainteInfo shadow-xl">
						<div className="mainteHeader">
							<div className='sub-title row'>
								<span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
								<span style={{ borderBottom: "1px solid black" }}>{"Time Keeper"}</span>
							</div>
							<h3>Time Keeper</h3>
						</div>
						<div className='p-4 space-y-4'>
							{/* Filters Section */}
							<div className="rounded-lg bg-white px-4 shadow-sm" style={{ border: '1px solid #d1d5db' }}>
								<div className="py-1">
									<h3 className="font-medium text-gray-500">Filters</h3>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-3 pt-3">
									<LocationIdSelectWithAll
										label="Location:"
										value={locationFilter}
										setValue={handleLocationFilterChange}
									/>
									<TimeKeeperSelectWithAll
										label="Time Keeper:"
										value={timeKeeperFilter}
										setValue={handleTimeKeeperFilterChange}
									/>
								</div>
							</div>

							<SearchBar handleFilter={handleFilter} />

							<Button icon={<AddIcon />}
								name={"Add Time Keeper"}
								className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
								onClick={openAddHandleClick}
							/>

							<CreateTable
								tableName={"Time Keepers"}
								headers={headers}
								accessors={accessors}
								data={data}
								actions={actions}
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								itemsPerPage={10}
								totalItems={data?.length}
							/>
						</div>
					</div>
				</div>
			</div>

			<AddEditForm
				isOpen={showAddForm}
				onClose={closeAddHandleClick}
				form={form}
				setForm={setForm}
				handleSubmit={handleSubmit}
				modalName={"Add Time Keeper"}
				mode="add"
				openEmployeeSelect={openEmployeeSelect}
				clearEmployee={clearEmployee}
				handleSelectEmployee={handleSelectEmployee}
			/>

			<AddEditForm
				isOpen={showEditForm}
				onClose={closeEditHandleClick}
				form={form}
				setForm={setForm}
				handleSubmit={handleUpdate}
				modalName={"Update Time Keeper"}
				mode="edit"
				openEmployeeSelect={openEmployeeSelect}
				clearEmployee={clearEmployee}
				handleSelectEmployee={handleSelectEmployee}
			/>

			<DeleteForm
				isOpen={showDeleteForm}
				onClose={closeDeleteHandleClick}
				handleDelete={handleDelete}
			/>

			{/* Use the reusable GeneralMessage component */}
			<GeneralMessage
				form={showMessage}
				message={message}
				isSuccess={isSuccess}
			/>

			{/* Combined loading spinner for both filter loading and request loading */}
			<LoadingSpinner loading={loadingRequest || loading || filterLoading} />
		</div>
	)
}

function AddEditForm({
	isOpen,
	modalName,
	onClose,
	form,
	setForm,
	handleSubmit,
	mode = "add", // "add", "edit"
	openEmployeeSelect,
	clearEmployee,
	handleSelectEmployee
}) {
	const [errorState, setErrorState] = useState({
		locationId: false,
		employee: false,
		shiftScheduleId: false
	});

	const submit = (e) => {
		e.preventDefault();

		// Validation
		const errors = {
			locationId: !form.locationId,
			employee: !form.employeePersonalInformationId,
			shiftScheduleId: !form.shiftScheduleId
		};

		setErrorState(errors);

		if (Object.values(errors).some(error => error)) {
			return;
		}

		handleSubmit(e);
		setErrorState({
			locationId: false,
			employee: false,
			shiftScheduleId: false
		});
	};

	const handleClose = () => {
		onClose();
		setErrorState({
			locationId: false,
			employee: false,
			shiftScheduleId: false
		});
	};

	const handleInputChange = (field, value) => {
		setForm(prev => ({
			...prev,
			[field]: value
		}));
	};

	if (!isOpen) return null;

	const isEditMode = mode === "edit";

	return (
		<>
			<div
				role="dialog"
				aria-modal="true"
				className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
			>
				<div className="bg-gray-50 w-full md:w-[90%] lg:w-[60%] xl:w-[50%] rounded-lg shadow-xl h-auto max-h-[90vh] flex flex-col relative">

					{/* Header */}
					<div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
						<h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
							<span className="text-xl text-gray-600">{modalName}</span>
						</h2>

						<button
							onClick={handleClose}
							className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
							aria-label="Close modal"
						>
							<CloseIcon fontSize="medium" />
						</button>
					</div>

					{/* Body */}
					<div className="p-6 overflow-y-auto flex-grow space-y-4">

						{/* Time Keeper Form */}
						<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
							<div className="py-1 border-b border-gray-300">
								<h3 className="font-medium text-gray-700">Time Keeper Information</h3>
							</div>
							<div className="grid grid-cols-1 gap-4 pb-3 pt-3">

								{/* Location Selection */}
								<div>
									<label className="block text-sm font-medium text-gray-600 mb-1">
										Location: <span className="text-red-500">*</span>
									</label>
									<LocationSelect
										locationId={form.locationId}
										setLocationId={(value) => handleInputChange('locationId', value)}
									/>
									{errorState.locationId && (
										<p className="text-red-500 text-xs mt-1">Please select a location</p>
									)}
								</div>

								{/* Employee Selection */}
								<EmployeePickerField
									label="Time Keeper:"
									employeePersonalInformationId={form.employeeData ? form.employeeData.id : ''}
									displayValue={form.employeeData ? `${form.employeeData.fullName}` : ""}
									onSelect={handleSelectEmployee}
									onClear={clearEmployee}
									error={errorState.employee && !form.employeeData}
									errorMessage="Please select a time keeper"
									placeholder="--Select time keeper--"
									allowClear={false}
								/>

								{/* Shift Schedule Selection */}
								<div>
									<label className="block text-sm font-medium text-gray-600 mb-1">
										Shift Schedule: <span className="text-red-500">*</span>
									</label>
									<ShiftScheduleSelect
										shiftScheduleId={form.shiftScheduleId}
										setShiftScheduleId={(value) => handleInputChange('shiftScheduleId', value)}
									/>
									{errorState.shiftScheduleId && (
										<p className="text-red-500 text-xs mt-1">Please select a shift schedule</p>
									)}
								</div>

							</div>
						</div>

					</div>

					{/* Footer */}
					<div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
						<button
							onClick={handleClose}
							className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
						>
							Cancel
						</button>

						<button
							onClick={submit}
							className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded border-none cursor-pointer transition-colors"
						>
							{isEditMode ? "Update" : "Save"}
						</button>
					</div>

				</div>
			</div>
		</>
	);
}

function LocationSelect({ locationId, setLocationId }) {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const response = await axios.get(domain + "/api/Location/active", {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});

			// Sort by description
			const sortedData = response.data.sort((a, b) =>
				a.description.localeCompare(b.description)
			);
			setData(sortedData);
		} catch (error) {
			console.error('Error fetching locations:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleChange = (e) => {
		setLocationId(e.target.value);
	};

	return (
		<Select
			value={locationId}
			onChange={handleChange}
			options={[
				{ value: "", label: "Choose location", disabled: true },
				...data.map(location => ({
					value: location.id,
					label: location.description
				}))
			]}
			placeholder="Choose location"
			isLoading={loading}
		/>
	);
}

function ShiftScheduleSelect({ shiftScheduleId, setShiftScheduleId }) {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const response = await axios.get(domain + "/api/ShiftSchedule", {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json'
				}
			});
			setData(response.data);
		} catch (error) {
			console.error('Error fetching shift schedules:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const formatTime = (time) => {
		if (!time) return "";

		try {
			const timePart = time.split('T')[1]?.substring(0, 5) || time.substring(0, 5);
			const [hours, minutes] = timePart.split(':');

			// Format as 12-hour time
			const hour = parseInt(hours, 10);
			const ampm = hour >= 12 ? 'PM' : 'AM';
			const hour12 = hour % 12 || 12;

			return `${hour12}:${minutes} ${ampm}`;
		} catch (error) {
			return time;
		}
	};

	const handleChange = (e) => {
		setShiftScheduleId(e.target.value);
	};

	return (
		<Select
			value={shiftScheduleId}
			onChange={handleChange}
			options={[
				{ value: "", label: "Choose shift schedule", disabled: true },
				...data.map(schedule => ({
					value: schedule.id,
					label: `${schedule.shiftName} (${formatTime(schedule.timeFrom)} - ${formatTime(schedule.timeTo)})`
				}))
			]}
			placeholder="Choose shift schedule"
			isLoading={loading}
		/>
	);
}

function DeleteForm({ isOpen, onClose, handleDelete }) {
	const handleClose = () => {
		onClose();
	};

	const handleDeleteConfirm = () => {
		handleDelete();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<>
			<div
				role="dialog"
				aria-modal="true"
				className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto"
			>
				<div className="bg-gray-50 w-full md:w-[90%] lg:w-[40%] xl:w-[30%] rounded-lg shadow-xl h-auto flex flex-col relative">

					{/* Header */}
					<div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
						<h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
							<span className="text-xl text-red-600">Delete Confirmation</span>
						</h2>

						<button
							onClick={handleClose}
							className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none"
							aria-label="Close modal"
						>
							<CloseIcon fontSize="medium" />
						</button>
					</div>

					{/* Body */}
					<div className="p-6 flex-grow">
						<div className="text-center py-4">
							<p className="text-lg font-medium text-gray-700 mb-2">
								Are you sure you want to delete this Time Keeper record?
							</p>
							<p className="text-sm text-gray-500">
								This action cannot be undone.
							</p>
						</div>
					</div>

					{/* Footer */}
					<div className="flex justify-center space-x-4 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
						<button
							onClick={handleClose}
							className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
						>
							Cancel
						</button>

						<button
							onClick={handleDeleteConfirm}
							className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
						>
							Delete
						</button>
					</div>

				</div>
			</div>
		</>
	);
}

export default TimeKeeper